<script setup>
import ModalUpdatePassword from "./ModalUpdatePassword.vue";
import ModalDeleteAccount from "./ModalDeleteAccount.vue";


const {
	curriculum,
	isDeleteAccountModalOpen,
	isUpdatePasswordModalOpen,
	passwordUpdated,
	tagEvent,
	toggleModals,
	user,
} = defineProps({
	curriculum: Object,
	isDeleteAccountModalOpen: Boolean,
	isUpdatePasswordModalOpen: Boolean,
	passwordUpdated: Function,
	tagEvent: Function,
	toggleModals: Function,
	user: Object,
});

let countRead = 0;
let countSaved = 0;
let countLiked = 0;
let countDisliked = 0;
let studyProgress = {};

// initialize
calculateStats();

function calculateStats() {
	const training = user.training;
	Object.keys(training).forEach(function(id) {
		if (training[id]?.read) {
			countRead +=1;
		}
		if (training[id]?.saved) {
			countSaved +=1;
		}
		if (training[id]?.like) {
			countLiked +=1;
		}
		if (training[id]?.dislike) {
			countDisliked +=1;
		}
	});

	studyProgress = curriculum.map((section) => {
		if (section.label === "Getting Started") {return};
		const total = section.articles.length;
		const label = section.label;
		const readCount = section.articles.filter(article => user.training?.[article.id]?.read).length;
		const progress = Math.round((readCount / total) * 100);
		return { label, progress, readCount, total };
	}).filter(Boolean);

	studyProgress.total = 0;
	studyProgress.readCount = 0;
	studyProgress.forEach((section) => {
		studyProgress.total += section.total;
		studyProgress.readCount += section.readCount;
	});
	studyProgress.progress = Math.round((studyProgress.readCount / studyProgress.total) * 100);
}

function formatDateWithOrdinal(isoDate) {
	const date = new Date(isoDate);
	const now = new Date();

	// Normalize time to avoid issues with timezones
	const dateString = date.toDateString();
	const nowString = now.toDateString();
	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	const yesterdayString = yesterday.toDateString();

	if (dateString === nowString) { return "today 🎉"; }
	else if (dateString === yesterdayString) { return "yesterday 👏"; }

	const month = date.toLocaleString('en-US', { month: 'long' });
	const day = date.getDate();
	const year = date.getFullYear();

	// Determine the ordinal suffix (st, nd, rd, th)
	const getOrdinalSuffix = (day) => {
			if (day >= 11 && day <= 13) return "th";
			const lastDigit = day % 10;
			return lastDigit === 1 ? "st" : lastDigit === 2 ? "nd" : lastDigit === 3 ? "rd" : "th";
	};

	const dayWithOrdinal = `${day}${getOrdinalSuffix(day)}`;
	const showYear = year !== now.getFullYear() ? `, ${year}` : '';

	return `${month} ${dayWithOrdinal}${showYear}`;
}
</script>

<template>
<div>
	<div id="profile-data">
		<h1>Account Info</h1>
		<blockquote class="my-0">
			<p class="my-0">We try to collect as little info as we can: It&#8217;s safer for you, and it&#8217;s less for us to keep track of.</p>
			<p class="mb-0">Here&#8217;s all the data that we have:</p>
		</blockquote>
		<p><label>Email:</label> <b>{{ user.email }}</b></p>
		<div class="d-flex-row">
			<button
				id="translate" class="btn secondary" title="Translate"
				@click="toggleModals('language-prompt')"
			>
				<svg width="24" height="16" viewBox="0 0 387 275" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M247 240L261.606 202M366.5 240L352.725 202M261.606 202L301.774 97.4986C304.098 91.4521 312.681 91.5346 314.889 97.6245L352.725 202M261.606 202H352.725" stroke="var(--bg1)" stroke-width="41" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M247 240L261.606 202M366.5 240L352.725 202M261.606 202L301.774 97.4986C304.098 91.4521 312.681 91.5346 314.889 97.6245L352.725 202M261.606 202H352.725" stroke="var(--text1)" stroke-width="20" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M21 39H108M188 39H160.5M160.5 39C153 91 117.5 149.5 39.5 182M160.5 39H108M60 87.5C80.5 139.5 135 170.5 165.5 182M108 39V21" stroke="var(--bg1)" stroke-width="41" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M21 39H108M188 39H160.5M160.5 39C153 91 117.5 149.5 39.5 182M160.5 39H108M60 87.5C80.5 139.5 135 170.5 165.5 182M108 39V21" stroke="var(--text1)" stroke-width="20" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M281 10L180 265" stroke="var(--link)" stroke-width="20" stroke-linecap="round"/>
				</svg>
				<span>Translate</span>
			</button>
			<button class="btn secondary" @click="toggleModals('update-password')">
				Change Password
			</button>
		</div>
	</div>
	<div id="details">
		<h2 class="mb-0">Stats</h2>
		<p class="mb-0">Joined {{ formatDateWithOrdinal(this.user.created) }}</p>
		<p class="mt-0">Curriculum Progress {{ studyProgress.progress }}%</p>
		<p class="my-0">
			<b>{{ countRead ?? 0 }}</b> Read
		</p>
		<p class="my-0">
			<b>{{ countSaved ?? 0 }}</b> Saved
		</p>
		<p class="my-0">
			<b>{{ countLiked ?? 0 }}</b> Liked
		</p>
		<p class="my-0">
			<b>{{ countDisliked ?? 0 }}</b> Disliked
		</p>
		<p class="my-0 hidden">
			<b>{{ user.sharedTraining ?? 0 }}</b> shared
		</p>
	</div>
	<div id="delete">
		<h2>Security Options</h2>
		<button class="btn secondary" @click="toggleModals('delete-account')">
			Delete Account
		</button>
		<ModalDeleteAccount
			:isDeleteAccountModalOpen="isDeleteAccountModalOpen"
			:user="user"
			:toggleModals="toggleModals"
			:tagEvent="tagEvent"
		/>
	</div>
	<ModalUpdatePassword
		:isUpdatePasswordModalOpen="isUpdatePasswordModalOpen"
		:passwordUpdated="passwordUpdated"
		:toggleModals="toggleModals"
	/>
</div>
</template>

<style lang="scss" scoped>
h1 {
	margin: .5em 0 .5rem;
}
#details, #delete {
	margin-top: 2em;
}
</style>
